import React, { useEffect, useRef, useState } from "react";

/**
 * Pieces shared by product mode's full-screen animations. The intro is a
 * dark takeover with the brand on top, a status block (headline, source
 * line, progress bar, counter) and a scene below; the export animation only
 * borrows the timing and the fade. The timing is the same three beats — a
 * landing stretch, a short "complete" beat, then a fade — and a tap anywhere
 * cuts it short.
 */

export const LEAVE_MS = 450;
const COMPLETE_SHARE = 0.18; // the tail of the run that shows "complete"
const COMPLETE_MIN_MS = 700;
const LANDING_MIN_MS = 600;

export const OVERLAY_CSS = `
.pm-overlay-enter { animation: pm-overlay-fade-in 300ms ease-out both; }
.pm-overlay-leave { animation: pm-overlay-fade-out ${LEAVE_MS}ms ease-in both; pointer-events: none; }
@keyframes pm-overlay-fade-in { from { opacity: 0; } to { opacity: 1; } }
@keyframes pm-overlay-fade-out { from { opacity: 1; transform: scale(1); } to { opacity: 0; transform: scale(1.03); } }
.pm-overlay-rise { animation: pm-overlay-rise 650ms cubic-bezier(.2,.8,.2,1) both; }
@keyframes pm-overlay-rise { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
.pm-overlay-glow { animation: pm-overlay-glow 3.2s ease-in-out infinite; }
@keyframes pm-overlay-glow {
  0%, 100% { opacity: .55; transform: translate(-50%, 0) scale(1); }
  50% { opacity: .9; transform: translate(-50%, 0) scale(1.08); }
}
.pm-overlay-bar { width: 0; animation: pm-overlay-bar linear both; }
@keyframes pm-overlay-bar { from { width: 0%; } to { width: 100%; } }
.pm-overlay-dot { animation: pm-overlay-dot 1.2s ease-in-out infinite; }
@keyframes pm-overlay-dot { 0%, 100% { opacity: .25; transform: translateY(0); } 50% { opacity: 1; transform: translateY(-3px); } }
.pm-overlay-check { animation: pm-overlay-check 500ms cubic-bezier(.2,.9,.3,1.3) both; }
@keyframes pm-overlay-check { from { opacity: 0; transform: scale(.4) rotate(-20deg); } to { opacity: 1; transform: scale(1) rotate(0); } }

/* Intro: tiles land one by one */
.pm-intro-tile { opacity: 0; animation: pm-intro-pop 480ms cubic-bezier(.2,.9,.3,1.25) both, pm-intro-burst 700ms ease-out both; }
@keyframes pm-intro-pop {
  0% { opacity: 0; transform: scale(.35) translateY(10px); filter: brightness(1.8) saturate(1.4); }
  55% { opacity: 1; transform: scale(1.1) translateY(0); }
  100% { opacity: 1; transform: scale(1); filter: brightness(1) saturate(1); }
}
@keyframes pm-intro-burst { 0% { box-shadow: 0 0 0 0 rgba(34,211,238,.9); } 100% { box-shadow: 0 0 0 10px rgba(34,211,238,0); } }
.pm-intro-scan {
  background: linear-gradient(to bottom, transparent, rgba(34,211,238,.14) 50%, transparent);
  background-size: 100% 28%;
  background-repeat: no-repeat;
  animation: pm-intro-scan 2.4s linear infinite;
}
@keyframes pm-intro-scan { from { background-position: 0 -40%; } to { background-position: 0 140%; } }

@media (prefers-reduced-motion: reduce) {
  .pm-intro-tile, .pm-overlay-rise, .pm-overlay-check { animation: none; opacity: 1; }
  .pm-overlay-glow, .pm-intro-scan, .pm-overlay-dot { animation: none; }
}
`;

/**
 * Timing for an overlay: the landing stretch (things arrive or leave while
 * the counter climbs), a short "complete" beat, then the fade-out.
 *
 * @returns {{ phase: "running"|"complete"|"leaving", isComplete: boolean, count: number, landingMs: number, leave: () => void }}
 */
export const useOverlayRun = ({ durationMs, total, onLeave, onDone }) => {
  const completeMs = Math.max(COMPLETE_MIN_MS, durationMs * COMPLETE_SHARE);
  const landingMs = Math.max(LANDING_MIN_MS, durationMs - completeMs);
  const productCount = Math.max(0, Number(total) || 0);

  const [count, setCount] = useState(0);
  const [phase, setPhase] = useState("running");

  const onLeaveRef = useRef(onLeave);
  const onDoneRef = useRef(onDone);
  onLeaveRef.current = onLeave;
  onDoneRef.current = onDone;
  const leftRef = useRef(false);
  const doneRef = useRef(false);
  const timersRef = useRef([]);

  const leave = () => {
    if (leftRef.current) return;
    leftRef.current = true;
    setPhase("leaving");
    onLeaveRef.current?.();
    timersRef.current.push(
      setTimeout(() => {
        if (doneRef.current) return;
        doneRef.current = true;
        onDoneRef.current?.();
      }, LEAVE_MS)
    );
  };

  // The counter climbs with the landing stretch.
  useEffect(() => {
    const start = performance.now();
    let frame = 0;
    const tick = () => {
      const progress = Math.min(1, (performance.now() - start) / landingMs);
      setCount(Math.round(progress * productCount));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [landingMs, productCount]);

  // Phases on a fixed schedule; a tap can cut it short.
  useEffect(() => {
    const timers = timersRef.current;
    timers.push(setTimeout(() => setPhase((current) => (current === "leaving" ? current : "complete")), landingMs));
    timers.push(setTimeout(leave, durationMs));
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { phase, isComplete: phase !== "running", count, landingMs, leave };
};

export const Dots = () => (
  <span className="inline-flex items-baseline gap-[3px] ml-1.5" aria-hidden="true">
    {[0, 1, 2].map((i) => (
      <span key={i} className="pm-overlay-dot inline-block w-[5px] h-[5px] rounded-full bg-white" style={{ animationDelay: `${i * 180}ms` }} />
    ))}
  </span>
);

export const CheckBadge = ({ className = "w-7 h-7" }) => (
  <span className={`pm-overlay-check inline-flex rounded-full bg-emerald-500/20 text-emerald-300 items-center justify-center ${className}`}>
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
    </svg>
  </span>
);

/** The fixed dark takeover: aurora glow, content, "Tap to skip". */
export const OverlayShell = ({ phase, onSkip, ariaLabel, children }) => (
  <div
    role="status"
    aria-live="polite"
    aria-label={ariaLabel}
    onClick={onSkip}
    className={`fixed inset-0 z-[150] flex flex-col items-center overflow-hidden bg-[#0a0a0b] text-white select-none ${
      phase === "leaving" ? "pm-overlay-leave" : "pm-overlay-enter"
    }`}
    style={{ WebkitTapHighlightColor: "transparent" }}
  >
    <style>{OVERLAY_CSS}</style>
    <div
      aria-hidden="true"
      className="pm-overlay-glow pointer-events-none absolute -top-28 left-1/2 w-[28rem] h-[28rem] rounded-full blur-3xl"
      style={{
        background:
          "radial-gradient(closest-side, rgba(96,165,250,.38), rgba(34,211,238,.2) 45%, rgba(192,132,252,.14) 70%, transparent)",
      }}
    />
    {children}
    <div className="relative pb-6 pt-3 text-[11px] text-gray-600">Tap to skip</div>
  </div>
);

/** Logo, name and domain — so viewers of a recording can find us. */
export const BrandBlock = () => (
  <div className="pm-overlay-rise relative flex flex-col items-center pt-14">
    <div className="w-16 h-16 rounded-2xl p-[2px] bg-gradient-to-br from-blue-400 via-cyan-400 to-purple-400 shadow-lg shadow-cyan-500/30">
      <div className="w-full h-full rounded-[14px] bg-white p-1 flex items-center justify-center overflow-hidden">
        <img src="/logo-256.png" alt="Ultimate Web Scraper" draggable={false} className="w-full h-full" />
      </div>
    </div>
    <div className="mt-3 text-xl font-bold tracking-tight bg-gradient-to-r from-blue-400 via-cyan-400 to-purple-400 bg-clip-text text-transparent">
      Ultimate Web Scraper
    </div>
    <div className="mt-1 text-[11px] uppercase tracking-[0.2em] text-gray-500">ultimatewebscraper.com</div>
  </div>
);

/**
 * Headline with animated dots (or the complete headline with a check), an
 * optional source line, the progress bar and the caller's count line.
 */
export const StatusBlock = ({ complete, headline, completeHeadline, subline, favicon, landingMs, countLine }) => {
  // Keep the dots on the headline's last word so they never wrap alone.
  const words = String(headline || "").split(" ");
  const lastWord = words.pop();
  return (
  <div className="pm-overlay-rise relative mt-8 w-full max-w-sm px-6 flex flex-col items-center" style={{ animationDelay: "150ms" }}>
    <div className="flex items-center gap-2 text-[26px] font-bold tracking-tight text-center">
      {complete ? (
        <>
          <CheckBadge />
          <span>{completeHeadline}</span>
        </>
      ) : (
        <span>
          {words.length > 0 && `${words.join(" ")} `}
          <span className="whitespace-nowrap">
            {lastWord}
            <Dots />
          </span>
        </span>
      )}
    </div>
    {subline && (
      <div className="mt-1 flex items-center gap-1.5 text-[13px] text-gray-400">
        {favicon && (
          <img
            src={favicon}
            alt=""
            className="w-3.5 h-3.5 rounded-sm"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        )}
        <span>{subline}</span>
      </div>
    )}
    <div className="mt-4 w-full h-1 rounded-full bg-white/[0.08] overflow-hidden">
      <div
        className={`pm-overlay-bar h-full rounded-full ${complete ? "bg-emerald-400" : "bg-gradient-to-r from-blue-400 via-cyan-400 to-purple-400"}`}
        style={{ animationDuration: `${landingMs}ms` }}
      />
    </div>
    <div className="mt-3 flex items-baseline gap-2">{countLine}</div>
  </div>
  );
};

/** Fetch the images ahead of their cue so they land already drawn. */
export const usePreloadImages = (urls) => {
  useEffect(() => {
    (urls || []).forEach((url) => {
      const img = new Image();
      img.referrerPolicy = "no-referrer";
      img.src = url;
    });
  }, [urls]);
};
